import { useState } from "react";
import classNames from "classnames";
import { get, has, isEmpty, isNil } from "lodash";
import PropTypes from "prop-types";
import {
  BiBlock,
  BiCheck,
  BiDownload,
  BiEnvelope,
  BiError,
  BiKey,
  BiLinkExternal,
  BiLock,
  BiLogoAws,
  BiLogoGoogle,
  BiLogoMicrosoft,
} from "react-icons/bi";
import { base64Of, bytesOf, readBytes, saveBytes } from "utils/helpers/bytes";
import { formatLocalDateTime } from "utils/helpers/date";
import { fill } from "utils/helpers/template";
import ExpiryPicker, { expiryDateOf, isExpiryValid } from "./ExpiryPicker";
import Segmented, { SEGMENTED_VARIANTS } from "./Segmented";
import { ENCRYPTION_MODES, ENCRYPTION_STATUSES } from "./setup";

export const SEED_METHODS = {
  BROWSER: "BROWSER",
  AWS_SHELL: "AWS_SHELL",
  TERMINAL: "TERMINAL",
  CLOUD_SHELL: "CLOUD_SHELL",
};

export const KEY_METHODS = Object.values(SEED_METHODS);

export const CREATORS = {
  HERE: "HERE",
  EMAIL: "EMAIL",
};

export const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const SHELL_LINKS = {
  [SEED_METHODS.AWS_SHELL]: "https://console.aws.amazon.com/cloudshell/home",
  [SEED_METHODS.CLOUD_SHELL]: "https://shell.cloud.google.com/?show=terminal",
};

export const CHOICE_TONES = {
  BLUE: "blue",
  AMBER: "amber",
  MUTED: "muted",
};

const SEED_BYTES = 32;

const SEED_FILE = "PlaintextKeyMaterial.bin";

const WRAPPING_KEY_FILE = "WrappingPublicKey.bin";

const ENCRYPTED_SEED_FILE = "EncryptedKeyMaterial.bin";

const EXPIRY_CHOICES = [false, true];

const GENERATE_COMMAND = `openssl rand -out ${SEED_FILE} ${SEED_BYTES}`;

const WRAP_COMMAND = [
  "openssl pkeyutl -encrypt \\",
  `  -in ${SEED_FILE} \\`,
  `  -out ${ENCRYPTED_SEED_FILE} \\`,
  `  -inkey ${WRAPPING_KEY_FILE} -keyform DER -pubin \\`,
  "  -pkeyopt rsa_padding_mode:oaep \\",
  "  -pkeyopt rsa_oaep_md:sha256 \\",
  "  -pkeyopt rsa_mgf1_md:sha256",
].join("\n");

const CLEANUP_COMMAND = `rm ${WRAPPING_KEY_FILE} ${SEED_FILE} ${ENCRYPTED_SEED_FILE}`;

const seedCommandsOf = (reimport) =>
  reimport ? WRAP_COMMAND : `${GENERATE_COMMAND}\n\n${WRAP_COMMAND}`;

const createCommandOf = (region) =>
  [
    "aws kms create-key \\",
    '  --description "Kayros integrations" \\',
    `  --region ${region}`,
  ].join("\n");

const wrapSeed = ({ seed, publicKey }) =>
  window.crypto.subtle
    .importKey(
      "spki",
      bytesOf(publicKey),
      { name: "RSA-OAEP", hash: "SHA-256" },
      false,
      ["encrypt"],
    )
    .then((key) =>
      window.crypto.subtle.encrypt({ name: "RSA-OAEP" }, key, seed),
    )
    .then((wrapped) => base64Of(new Uint8Array(wrapped)));

export const CopyButton = ({ text, labels }) => {
  const [copied, setCopied] = useState(false);
  const copy = () =>
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(text))
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  return (
    <button type="button" className="onboarding__copy" onClick={copy}>
      {copied ? labels.COPIED : labels.COPY}
    </button>
  );
};

CopyButton.propTypes = {
  text: PropTypes.string.isRequired,
  labels: PropTypes.object.isRequired,
};

export const CodeBlock = ({ code, labels }) => (
  <div className="onboarding__code">
    <CopyButton text={code} labels={labels} />
    <pre>{code}</pre>
  </div>
);

CodeBlock.propTypes = {
  code: PropTypes.string.isRequired,
  labels: PropTypes.object.isRequired,
};

export const Step = ({ number, title, children }) => (
  <li className="onboarding__step-card">
    <span className="onboarding__step-number">{number}</span>
    <div className="onboarding__step-body">
      <h3 className="onboarding__section-title">{title}</h3>
      {children}
    </div>
  </li>
);

Step.propTypes = {
  number: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export const Choice = ({
  icon,
  tone,
  tag,
  title,
  text,
  needs,
  action,
  onClick,
}) => {
  const soon = tone === CHOICE_TONES.MUTED;
  return (
    <button
      type="button"
      className={classNames("onboarding__choice", {
        "onboarding__choice--soon": soon,
      })}
      disabled={soon}
      onClick={onClick}
    >
      <span className="onboarding__choice-head">
        <span
          className={classNames(
            "onboarding__choice-icon",
            `onboarding__choice-icon--${tone}`,
          )}
        >
          {icon}
        </span>
        <span
          className={classNames("onboarding__chip", {
            "onboarding__chip--success": tone === CHOICE_TONES.BLUE,
          })}
        >
          {tag}
        </span>
      </span>
      <strong>{title}</strong>
      <span>{text}</span>
      {needs ? <span className="onboarding__choice-needs">{needs}</span> : null}
      {action ? (
        <span className="onboarding__choice-action">{action}</span>
      ) : null}
    </button>
  );
};

Choice.propTypes = {
  icon: PropTypes.node.isRequired,
  tone: PropTypes.oneOf(Object.values(CHOICE_TONES)).isRequired,
  tag: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  needs: PropTypes.string,
  action: PropTypes.string,
  onClick: PropTypes.func,
};

Choice.defaultProps = {
  needs: null,
  action: null,
  onClick: null,
};

const AwsAccountFlow = ({ encryption, labels, busy, onLink, onBack }) => {
  const [arn, setArn] = useState("");
  const AWS = labels.AWS;
  return (
    <div className="onboarding__stack">
      <ol className="onboarding__step-list">
        <Step number={1} title={AWS.STEP_CREATE}>
          <p className="onboarding__paragraph">
            {fill(AWS.STEP_CREATE_TEXT, { region: encryption.region })}
          </p>
          <CodeBlock
            code={createCommandOf(encryption.region)}
            labels={labels}
          />
        </Step>
        <Step number={2} title={AWS.STEP_ALLOW}>
          <p className="onboarding__paragraph">{AWS.STEP_ALLOW_TEXT}</p>
          <div className="onboarding__identity">
            <span className="onboarding__identity-text">
              <span>{AWS.IDENTITY}</span>
              <code>{encryption.identity}</code>
            </span>
            <CopyButton text={encryption.identity || ""} labels={labels} />
          </div>
          <CodeBlock code={encryption.keyPolicy || ""} labels={labels} />
        </Step>
        <Step number={3} title={AWS.STEP_LINK}>
          <div className="onboarding__field">
            <label htmlFor="organization-key-arn">{AWS.ARN}</label>
            <div className="onboarding__key-row">
              <input
                id="organization-key-arn"
                type="text"
                spellCheck={false}
                autoComplete="off"
                placeholder={`arn:aws:kms:${encryption.region}:…:key/…`}
                className="onboarding__input onboarding__input--mono"
                value={arn}
                onChange={({ target }) => setArn(target.value.trim())}
              />
            </div>
          </div>
        </Step>
      </ol>
      <div className="onboarding__footer onboarding__footer--flat">
        <button
          type="button"
          className="onboarding__button onboarding__button--outline"
          onClick={onBack}
        >
          {labels.BACK}
        </button>
        <button
          type="button"
          className="onboarding__button onboarding__button--primary onboarding__push"
          disabled={isEmpty(arn) || busy}
          onClick={() => onLink(arn)}
        >
          {busy ? AWS.VERIFYING : AWS.VERIFY}
        </button>
      </div>
    </div>
  );
};

AwsAccountFlow.propTypes = {
  encryption: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  busy: PropTypes.bool.isRequired,
  onLink: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export const MethodPicker = ({
  method,
  methods,
  labels,
  reimport,
  onChange,
}) => (
  <>
    <Segmented
      variant={SEGMENTED_VARIANTS.WRAP}
      value={method}
      options={methods.map((option) => ({
        value: option,
        label: get(labels.METHODS, option),
      }))}
      onChange={onChange}
    />
    <p className="onboarding__paragraph">
      {get(
        labels.METHOD_HINTS,
        reimport && method === SEED_METHODS.BROWSER
          ? "BROWSER_REIMPORT"
          : method,
      )}
    </p>
  </>
);

MethodPicker.propTypes = {
  method: PropTypes.string.isRequired,
  methods: PropTypes.arrayOf(PropTypes.string).isRequired,
  labels: PropTypes.object.isRequired,
  reimport: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

const BrowserSeed = ({
  labels,
  reimport,
  busy,
  prepared,
  onPrepare,
  onPrepared,
}) => {
  const IMPORT = labels.IMPORT;
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  const wrap = ({ seed, name, generated }) => {
    setWorking(true);
    setError(null);
    return onPrepare()
      .then((parameters) => {
        if (isNil(parameters)) return null;
        return wrapSeed({ seed, publicKey: parameters.publicKey }).then(
          (content) => {
            if (generated) saveBytes(seed, SEED_FILE);
            onPrepared({
              seed,
              name,
              generated,
              content,
            });
          },
        );
      })
      .catch(() => setError(IMPORT.GENERATE_FAILED))
      .finally(() => setWorking(false));
  };

  const generate = () =>
    wrap({
      seed: window.crypto.getRandomValues(new Uint8Array(SEED_BYTES)),
      name: SEED_FILE,
      generated: true,
    });

  const reuse = ({ target }) => {
    const [picked] = target.files || [];
    target.value = "";
    if (!picked) return null;
    return readBytes(picked)
      .then((seed) =>
        seed.length === SEED_BYTES
          ? wrap({ seed, name: picked.name, generated: false })
          : setError(IMPORT.SEED_INVALID),
      )
      .catch(() => setError(IMPORT.UPLOAD_FAILED));
  };

  const reuseButton = (
    <label className="onboarding__button onboarding__button--outline onboarding__file-button">
      {IMPORT.REUSE}
      <input
        type="file"
        className="onboarding__file"
        disabled={busy || working}
        onChange={reuse}
      />
    </label>
  );

  return (
    <>
      <p className="onboarding__paragraph">
        {reimport ? IMPORT.REUSE_TEXT : IMPORT.GENERATE_TEXT}
      </p>
      <div className="onboarding__card-actions">
        {reimport ? null : (
          <button
            type="button"
            className="onboarding__button onboarding__button--outline onboarding__button--icon"
            disabled={busy || working}
            onClick={generate}
          >
            <BiKey size={16} />
            {working ? IMPORT.GENERATING : IMPORT.GENERATE}
          </button>
        )}
        {reuseButton}
      </div>
      {prepared ? (
        <p className="onboarding__success-line">
          <BiCheck size={16} aria-hidden="true" />
          {prepared.generated
            ? IMPORT.GENERATED
            : fill(IMPORT.REUSED, { name: prepared.name })}
        </p>
      ) : null}
      {prepared && prepared.generated ? (
        <button
          type="button"
          className="onboarding__button onboarding__button--outline onboarding__button--small onboarding__button--icon onboarding__start"
          onClick={() => saveBytes(prepared.seed, SEED_FILE)}
        >
          <BiDownload size={15} />
          {IMPORT.SAVE_AGAIN}
        </button>
      ) : null}
      {error ? <p className="onboarding__error">{error}</p> : null}
      <p className="onboarding__notice onboarding__notice--warning onboarding__notice--compact">
        <BiError size={18} aria-hidden="true" />
        <span>{reimport ? IMPORT.KEEP : IMPORT.HANDOVER}</span>
      </p>
    </>
  );
};

BrowserSeed.propTypes = {
  labels: PropTypes.object.isRequired,
  reimport: PropTypes.bool.isRequired,
  busy: PropTypes.bool.isRequired,
  prepared: PropTypes.object,
  onPrepare: PropTypes.func.isRequired,
  onPrepared: PropTypes.func.isRequired,
};

BrowserSeed.defaultProps = {
  prepared: null,
};

const ShellSeed = ({ method, labels, reimport }) => {
  const IMPORT = labels.IMPORT;
  const hosted = has(SHELL_LINKS, method);
  return (
    <>
      <p className="onboarding__paragraph">
        {get(reimport ? IMPORT.WRAP_TEXTS_REIMPORT : IMPORT.WRAP_TEXTS, method)}
      </p>
      {hosted ? (
        <a
          className="onboarding__button onboarding__button--outline onboarding__button--icon onboarding__start"
          href={SHELL_LINKS[method]}
          target="_blank"
          rel="noopener noreferrer"
        >
          <BiLinkExternal size={16} />
          {get(IMPORT.OPEN_SHELL, method)}
        </a>
      ) : null}
      <CodeBlock code={seedCommandsOf(reimport)} labels={labels} />
      {hosted ? (
        <>
          <p className="onboarding__paragraph">
            {fill(get(IMPORT.SHELL_AFTER, method), {
              files: reimport
                ? IMPORT.SHELL_FILES_REIMPORT
                : IMPORT.SHELL_FILES,
            })}
          </p>
          <CodeBlock code={CLEANUP_COMMAND} labels={labels} />
        </>
      ) : null}
      <p className="onboarding__notice onboarding__notice--warning onboarding__notice--compact">
        <BiError size={18} aria-hidden="true" />
        <span>{IMPORT.KEEP}</span>
      </p>
    </>
  );
};

ShellSeed.propTypes = {
  method: PropTypes.string.isRequired,
  labels: PropTypes.object.isRequired,
  reimport: PropTypes.bool.isRequired,
};

export const CreatorPicker = ({ value, labels, onChange }) => (
  <>
    <Segmented
      value={value}
      options={Object.values(CREATORS).map((option) => ({
        value: option,
        label: get(labels.CREATORS, option),
      }))}
      onChange={onChange}
    />
    <p className="onboarding__paragraph">{get(labels.CREATOR_HINTS, value)}</p>
  </>
);

CreatorPicker.propTypes = {
  value: PropTypes.string.isRequired,
  labels: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export const Recipient = ({ adminEmail, value, labels, onChange }) => {
  const [other, setOther] = useState(isEmpty(adminEmail));
  const choose = (toOther) => {
    setOther(toOther);
    onChange(toOther ? "" : adminEmail);
  };
  return (
    <>
      {isEmpty(adminEmail) ? null : (
        <Segmented
          variant={SEGMENTED_VARIANTS.WRAP}
          value={other}
          options={[
            { value: false, label: labels.TO_ADMIN },
            { value: true, label: labels.TO_OTHER },
          ]}
          onChange={choose}
        />
      )}
      {other ? null : (
        <p className="onboarding__paragraph">
          {fill(labels.TO_ADMIN_HINT, { email: adminEmail })}
        </p>
      )}
      {other ? (
        <div className="onboarding__field">
          <label htmlFor="handover-recipient">{labels.EMAIL}</label>
          <input
            id="handover-recipient"
            type="email"
            autoComplete="off"
            className="onboarding__input"
            placeholder={labels.EMAIL_PLACEHOLDER}
            value={value}
            onChange={({ target }) => onChange(target.value.trim())}
          />
        </div>
      ) : null}
      <p className="onboarding__hint">{labels.HINT}</p>
    </>
  );
};

Recipient.propTypes = {
  adminEmail: PropTypes.string,
  value: PropTypes.string.isRequired,
  labels: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

Recipient.defaultProps = {
  adminEmail: "",
};

export const HandoverStatus = ({ handover, labels, busy, onResend }) => (
  <div
    className={classNames(
      "onboarding__notice onboarding__notice--action",
      handover.open
        ? "onboarding__notice--info"
        : "onboarding__notice--warning",
    )}
  >
    <BiEnvelope size={20} aria-hidden="true" />
    <span>
      {handover.open
        ? fill(labels.SENT, {
            email: handover.email,
            date: formatLocalDateTime(handover.expiresAt),
          })
        : fill(labels.EXPIRED, { email: handover.email })}
    </span>
    <button
      type="button"
      className="onboarding__button onboarding__button--outline onboarding__button--small"
      disabled={busy}
      onClick={() => onResend(handover.email)}
    >
      {labels.RESEND}
    </button>
  </div>
);

HandoverStatus.propTypes = {
  handover: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  busy: PropTypes.bool.isRequired,
  onResend: PropTypes.func.isRequired,
};

export const ImportFlow = ({
  encryption,
  labels,
  reimport,
  busy,
  adminEmail,
  onPrepare,
  onImport,
  onSendHandover,
  onBack,
}) => {
  const IMPORT = labels.IMPORT;
  const HANDOVER = labels.HANDOVER || {};
  const [method, setMethod] = useState(SEED_METHODS.BROWSER);
  const [creator, setCreator] = useState(CREATORS.HERE);
  const [recipient, setRecipient] = useState(adminEmail || "");
  const [downloadedUntil, setDownloadedUntil] = useState(
    encryption.importValidTo,
  );
  const [file, setFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [prepared, setPrepared] = useState(null);
  const [expiry, setExpiry] = useState({ on: false, date: "" });
  const browser = method === SEED_METHODS.BROWSER;

  const download = () =>
    onPrepare().then((parameters) => {
      if (isNil(parameters)) return;
      saveBytes(bytesOf(parameters.publicKey), WRAPPING_KEY_FILE);
      setDownloadedUntil(parameters.validTo);
      setPrepared(null);
    });

  const pick = ({ target }) => {
    const [picked] = target.files || [];
    target.value = "";
    if (!picked) return null;
    setUploadError(null);
    return readBytes(picked)
      .then((bytes) => setFile({ name: picked.name, content: base64Of(bytes) }))
      .catch(() => setUploadError(IMPORT.UPLOAD_FAILED));
  };

  const sendable = !isNil(onSendHandover);
  const emailing = sendable && creator === CREATORS.EMAIL;
  const material = browser ? get(prepared, "content") : get(file, "content");
  const ready = emailing
    ? EMAIL_FORMAT.test(recipient)
    : !isNil(material) &&
      (browser || !isNil(downloadedUntil)) &&
      isExpiryValid(expiry);

  const submit = () =>
    emailing
      ? onSendHandover(recipient)
      : onImport({
          encryptedKeyMaterial: material,
          expiresAt: expiryDateOf(expiry),
        });

  const [idleLabel, busyLabel] = emailing
    ? [HANDOVER.SEND, HANDOVER.SENDING]
    : [IMPORT.SUBMIT, IMPORT.SUBMITTING];

  const importSteps = [
    {
      key: "METHOD",
      title: reimport ? IMPORT.STEP_METHOD_REIMPORT : IMPORT.STEP_METHOD,
      body: (
        <MethodPicker
          method={method}
          methods={KEY_METHODS}
          labels={IMPORT}
          reimport={reimport}
          onChange={setMethod}
        />
      ),
    },
    ...(browser
      ? [
          {
            key: "BROWSER",
            title: reimport ? IMPORT.STEP_REUSE : IMPORT.STEP_GENERATE,
            body: (
              <BrowserSeed
                labels={labels}
                reimport={reimport}
                busy={busy}
                prepared={prepared}
                onPrepare={onPrepare}
                onPrepared={(next) => {
                  setPrepared(next);
                  setDownloadedUntil(null);
                }}
              />
            ),
          },
        ]
      : [
          {
            key: "DOWNLOAD",
            title: IMPORT.STEP_DOWNLOAD,
            body: (
              <>
                <p className="onboarding__paragraph">
                  {IMPORT.STEP_DOWNLOAD_TEXT}
                </p>
                <button
                  type="button"
                  className="onboarding__button onboarding__button--outline onboarding__button--icon onboarding__start"
                  disabled={busy}
                  onClick={download}
                >
                  <BiDownload size={16} />
                  {isNil(downloadedUntil)
                    ? IMPORT.DOWNLOAD
                    : fill(IMPORT.DOWNLOADED, {
                        date: formatLocalDateTime(downloadedUntil),
                      })}
                </button>
              </>
            ),
          },
          {
            key: "WRAP",
            title: IMPORT.STEP_WRAP,
            body: (
              <ShellSeed method={method} labels={labels} reimport={reimport} />
            ),
          },
          {
            key: "UPLOAD",
            title: IMPORT.STEP_UPLOAD,
            body: (
              <>
                <div className="onboarding__logo">
                  <span className="onboarding__logo-text">
                    <strong>{ENCRYPTED_SEED_FILE}</strong>
                    <span>
                      {isNil(file)
                        ? IMPORT.UPLOAD_HINT
                        : fill(IMPORT.UPLOAD_READY, { name: file.name })}
                    </span>
                  </span>
                  <label className="onboarding__button onboarding__button--outline onboarding__file-button">
                    {IMPORT.PICK}
                    <input
                      type="file"
                      className="onboarding__file"
                      onChange={pick}
                    />
                  </label>
                </div>
                {uploadError ? (
                  <p className="onboarding__error">{uploadError}</p>
                ) : null}
              </>
            ),
          },
        ]),
    {
      key: "EXPIRY",
      title: IMPORT.STEP_EXPIRY,
      body: (
        <ExpiryPicker
          id="organization-key-expiry"
          expiry={expiry}
          labels={IMPORT}
          choices={EXPIRY_CHOICES}
          onChange={setExpiry}
        />
      ),
    },
  ];

  const creatorStep = {
    key: "CREATOR",
    title: IMPORT.STEP_CREATOR,
    body: (
      <CreatorPicker value={creator} labels={IMPORT} onChange={setCreator} />
    ),
  };

  const recipientStep = {
    key: "RECIPIENT",
    title: HANDOVER.STEP,
    body: (
      <Recipient
        adminEmail={adminEmail}
        value={recipient}
        labels={HANDOVER}
        onChange={setRecipient}
      />
    ),
  };

  const steps = [
    ...(sendable ? [creatorStep] : []),
    ...(emailing ? [recipientStep] : importSteps),
  ];

  return (
    <div className="onboarding__stack">
      <ol className="onboarding__step-list">
        {steps.map((step, index) => (
          <Step key={step.key} number={index + 1} title={step.title}>
            {step.body}
          </Step>
        ))}
      </ol>
      <div className="onboarding__footer onboarding__footer--flat">
        {onBack ? (
          <button
            type="button"
            className="onboarding__button onboarding__button--outline"
            onClick={onBack}
          >
            {labels.BACK}
          </button>
        ) : null}
        <button
          type="button"
          className="onboarding__button onboarding__button--primary onboarding__push"
          disabled={!ready || busy}
          onClick={submit}
        >
          {busy ? busyLabel : idleLabel}
        </button>
      </div>
    </div>
  );
};

ImportFlow.propTypes = {
  encryption: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  reimport: PropTypes.bool.isRequired,
  busy: PropTypes.bool.isRequired,
  adminEmail: PropTypes.string,
  onPrepare: PropTypes.func.isRequired,
  onImport: PropTypes.func.isRequired,
  onSendHandover: PropTypes.func,
  onBack: PropTypes.func,
};

ImportFlow.defaultProps = {
  adminEmail: "",
  onSendHandover: null,
  onBack: null,
};

const ActiveKey = ({ encryption, labels, busy, onVerify, onChange }) => {
  const ACTIVE = labels.ACTIVE;
  const imported = encryption.mode === ENCRYPTION_MODES.IMPORTED;
  const revoked = encryption.status === ENCRYPTION_STATUSES.REVOKED;
  return (
    <div className="onboarding__stack onboarding__stack--wide">
      {revoked ? (
        <div className="onboarding__banner onboarding__banner--danger">
          <span className="onboarding__banner-icon" aria-hidden="true">
            <BiBlock size={20} />
          </span>
          <span className="onboarding__banner-text">
            <span className="onboarding__banner-title">
              {ACTIVE.REVOKED_TITLE}
              <span className="onboarding__badge onboarding__badge--danger">
                {ACTIVE.REVOKED_TAG}
              </span>
            </span>
            <span>{imported ? ACTIVE.REVOKED_IMPORT : ACTIVE.REVOKED_AWS}</span>
            <button
              type="button"
              className="onboarding__button onboarding__button--outline onboarding__button--small onboarding__start"
              disabled={busy}
              onClick={onVerify}
            >
              {ACTIVE.REVERIFY}
            </button>
          </span>
        </div>
      ) : (
        <div className="onboarding__banner onboarding__banner--success">
          <span className="onboarding__banner-icon" aria-hidden="true">
            <BiCheck size={20} />
          </span>
          <span className="onboarding__banner-text">
            <span className="onboarding__banner-title">
              {ACTIVE.TITLE}
              <span className="onboarding__badge onboarding__badge--done">
                {imported ? ACTIVE.MODE_IMPORT : ACTIVE.MODE_AWS}
              </span>
            </span>
            <span>
              {ACTIVE.TEXT}{" "}
              {encryption.verifiedAt
                ? fill(ACTIVE.VERIFIED, {
                    date: formatLocalDateTime(encryption.verifiedAt),
                  })
                : null}
            </span>
          </span>
        </div>
      )}
      <dl className="onboarding__facts">
        <div>
          <dt>{ACTIVE.KEY}</dt>
          <dd className="onboarding__mono">{encryption.keyArn}</dd>
        </div>
        <div>
          <dt>{ACTIVE.KEPT_BY}</dt>
          <dd>{imported ? ACTIVE.KEPT_IMPORT : ACTIVE.KEPT_AWS}</dd>
        </div>
        <div>
          <dt>{ACTIVE.EXPIRY}</dt>
          <dd>
            {encryption.expiresAt
              ? formatLocalDateTime(encryption.expiresAt)
              : ACTIVE.NO_EXPIRY}
          </dd>
        </div>
      </dl>
      <section className="onboarding__section onboarding__section--tight">
        <h3 className="onboarding__section-title">{ACTIVE.CONTROLS}</h3>
        <ul className="onboarding__dots onboarding__dots--blue">
          {(imported ? ACTIVE.CONTROLS_IMPORT : ACTIVE.CONTROLS_AWS).map(
            (item) => (
              <li key={item}>{item}</li>
            ),
          )}
        </ul>
      </section>
      <div className="onboarding__card-actions">
        <button
          type="button"
          className="onboarding__button onboarding__button--outline"
          disabled={busy}
          onClick={onVerify}
        >
          {ACTIVE.VERIFY}
        </button>
        <button
          type="button"
          className="onboarding__button onboarding__button--outline"
          onClick={() => onChange(imported ? ENCRYPTION_MODES.IMPORTED : null)}
        >
          {imported ? ACTIVE.REIMPORT : ACTIVE.CHANGE}
        </button>
      </div>
    </div>
  );
};

ActiveKey.propTypes = {
  encryption: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  busy: PropTypes.bool.isRequired,
  onVerify: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

const EncryptionKey = ({
  encryption,
  labels,
  busy,
  adminEmail,
  onLink,
  onPrepare,
  onImport,
  onVerify,
  onSendHandover,
}) => {
  const configured = [
    ENCRYPTION_STATUSES.ACTIVE,
    ENCRYPTION_STATUSES.REVOKED,
  ].includes(encryption.status);
  const unavailable = isNil(encryption.region) || isNil(encryption.identity);
  const awaiting = encryption.status === ENCRYPTION_STATUSES.AWAITING_IMPORT;
  const handover = encryption.handover;
  const linkOpen = get(handover, "open", false);
  const [flow, setFlow] = useState(
    awaiting && !linkOpen ? ENCRYPTION_MODES.IMPORTED : undefined,
  );
  const banner =
    !isNil(handover) && (linkOpen || !configured) ? (
      <HandoverStatus
        handover={handover}
        labels={labels.HANDOVER}
        busy={busy}
        onResend={onSendHandover}
      />
    ) : null;

  if (unavailable && !configured)
    return (
      <p className="onboarding__notice onboarding__notice--danger">
        <BiBlock size={18} aria-hidden="true" />
        <span>{labels.UNAVAILABLE}</span>
      </p>
    );

  if (configured && isNil(flow))
    return (
      <>
        {banner}
        <ActiveKey
          encryption={encryption}
          labels={labels}
          busy={busy}
          onVerify={onVerify}
          onChange={(mode) => setFlow(mode || "")}
        />
      </>
    );

  const back = () => setFlow(configured ? undefined : "");
  const done = (result) => {
    if (get(result, "status") === ENCRYPTION_STATUSES.ACTIVE)
      setFlow(undefined);
  };

  if (flow === ENCRYPTION_MODES.ORGANIZATION_ACCOUNT)
    return (
      <AwsAccountFlow
        encryption={encryption}
        labels={labels}
        busy={busy}
        onLink={(arn) => onLink(arn).then(done)}
        onBack={back}
      />
    );

  if (flow === ENCRYPTION_MODES.IMPORTED)
    return (
      <ImportFlow
        encryption={encryption}
        labels={labels}
        reimport={configured && encryption.mode === ENCRYPTION_MODES.IMPORTED}
        busy={busy}
        adminEmail={adminEmail}
        onPrepare={onPrepare}
        onImport={(material) => onImport(material).then(done)}
        onSendHandover={(recipient) =>
          onSendHandover(recipient).then((result) => {
            if (!isNil(result)) back();
          })
        }
        onBack={back}
      />
    );

  return (
    <div className="onboarding__stack">
      {banner}
      <p className="onboarding__notice onboarding__notice--info">
        <BiLock size={20} aria-hidden="true" />
        <span>{labels.INTRO}</span>
      </p>
      <div className="onboarding__choices">
        <Choice
          icon={<BiLogoAws size={22} />}
          tone={CHOICE_TONES.BLUE}
          tag={labels.AWS.TAG}
          title={labels.AWS.TITLE}
          text={labels.AWS.TEXT}
          needs={labels.AWS.NEEDS}
          action={labels.CHOOSE}
          onClick={() => setFlow(ENCRYPTION_MODES.ORGANIZATION_ACCOUNT)}
        />
        <Choice
          icon={<BiKey size={20} />}
          tone={CHOICE_TONES.AMBER}
          tag={labels.IMPORT.TAG}
          title={labels.IMPORT.TITLE}
          text={labels.IMPORT.TEXT}
          needs={labels.IMPORT.NEEDS}
          action={labels.CHOOSE}
          onClick={() => setFlow(ENCRYPTION_MODES.IMPORTED)}
        />
        <Choice
          icon={<BiLogoMicrosoft size={20} />}
          tone={CHOICE_TONES.MUTED}
          tag={labels.SOON}
          title={labels.AZURE.TITLE}
          text={labels.AZURE.TEXT}
        />
        <Choice
          icon={<BiLogoGoogle size={20} />}
          tone={CHOICE_TONES.MUTED}
          tag={labels.SOON}
          title={labels.GOOGLE.TITLE}
          text={labels.GOOGLE.TEXT}
        />
      </div>
    </div>
  );
};

EncryptionKey.propTypes = {
  encryption: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  busy: PropTypes.bool.isRequired,
  adminEmail: PropTypes.string,
  onLink: PropTypes.func.isRequired,
  onPrepare: PropTypes.func.isRequired,
  onImport: PropTypes.func.isRequired,
  onVerify: PropTypes.func.isRequired,
  onSendHandover: PropTypes.func.isRequired,
};

EncryptionKey.defaultProps = {
  adminEmail: "",
};

export default EncryptionKey;
